import React, { useCallback } from 'react';
import { Loader2, X } from 'lucide-react';
import { ModalShell } from './modal';
import { AppModalProps } from '../model';

const AppModal: React.FC<AppModalProps> = ({
  isOpen,
  title,
  description,
  children,
  formId,
  actions,
  hideFooter = false,
  maxWidth,
  variant = 'default',
  onClose,
  closeOnOverlayClick = false,
  headerAction,
}) => {
  const getCloseHandler = useCallback(() => {
    if (onClose) return onClose;
    return actions?.find(
      (a) => a.type === 'button' && a.variant === 'secondary'
    )?.onClick;
  }, [onClose, actions]);

  const renderFooter = useCallback(() => {
    if (hideFooter || !actions?.length) return null;

    const orderedActions = [...actions].sort((a, b) => {
      const rank = (variant?: 'primary' | 'secondary') =>
        variant === 'secondary' ? 0 : 1;
      return rank(a.variant) - rank(b.variant);
    });

    return (
      <div className="flex justify-end gap-3 pt-4">
        {orderedActions.map((action) => {
          const isDisabled = action.disabled || action.loading;
          const baseClass =
            action.variant === 'secondary' ? 'btn-secondary' : 'btn-primary';
          return (
            <button
              key={action.label}
              onClick={action.onClick}
              type={action.type || 'button'}
              form={action.type === 'submit' ? formId : undefined}
              disabled={isDisabled}
              className={`${baseClass} disabled:cursor-not-allowed`}
            >
              <span className="inline-flex items-center gap-2">
                {action.loading && <Loader2 className="h-4 w-4 animate-spin" />}
                <span>{action.label}</span>
              </span>
            </button>
          );
        })}
      </div>
    );
  }, [actions, formId, hideFooter]);

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={getCloseHandler()}
      closeOnOverlayClick={closeOnOverlayClick}
      ariaLabel={title ?? 'Dialog'}
      panelClassName={`w-full max-h-[90vh] overflow-hidden flex flex-col ${
        maxWidth ?? 'max-w-lg'
      }`}
    >
      {(title || description || onClose) && (
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            {title && (
              <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                {title}
              </h3>
            )}
            {description && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                {description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {headerAction}
            <button
              type="button"
              onClick={getCloseHandler()}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-900 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <div
        className={`px-6 py-4 overflow-y-auto custom-scroll ${
          variant === 'form' ? 'flex-1' : ''
        }`}
        style={
          variant === 'form' ? { maxHeight: 'calc(100vh - 12rem)' } : undefined
        }
      >
        {children}
        {renderFooter()}
      </div>
    </ModalShell>
  );
};

export default AppModal;
